import { useEffect, useRef } from 'react';

/** Consulta também em segundo plano, com backoff limitado; foco/rede retomam imediatamente. */
export function useBackgroundRefresh(refresh: () => unknown, enabled = true, { poll = true }: { poll?: boolean } = {}) {
  const current = useRef(refresh);
  current.current = refresh;
  useEffect(() => {
    if (!enabled) return;
    let alive = true, running = false, queued = false;
    let delay = 30_000;
    let timer: ReturnType<typeof setTimeout>;
    let resume: ReturnType<typeof setTimeout>;
    const run = async () => {
      if (running) { queued = true; return; }
      running = true;
      clearTimeout(timer);
      let changed = false;
      try { changed = await current.current() === true; } catch { /* o consumidor preserva seu erro e último dado */ }
      finally {
        running = false;
        if (alive) {
          if (queued) { queued = false; delay = 30_000; timer = setTimeout(() => void run(), 0); }
          else if (poll) { delay = changed ? 30_000 : Math.min(120_000, delay * 2); timer = setTimeout(() => void run(), delay); }
        }
      }
    };
    const wake = () => {
      if (document.visibilityState === 'hidden') return;
      clearTimeout(timer); clearTimeout(resume); delay = 30_000;
      resume = setTimeout(() => { if (alive) void run(); }, 0);
    };
    if (poll) timer = setTimeout(() => void run(), delay);
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    return () => {
      alive = false; clearTimeout(timer); clearTimeout(resume);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
    };
  }, [enabled, poll]);
}
